import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Heart, HandHeart, Mail, LogOut, Download, TrendingUp, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const toCsv = (rows, columns) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const header = columns.map((c) => esc(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => esc(r[c.key])).join(',')).join('\n');
  return `${header}\n${body}`;
};

const downloadCsv = (filename, rows, columns) => {
  const blob = new Blob([toCsv(rows, columns)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="border-0 shadow-md">
    <CardContent className="pt-6 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [donations, setDonations] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('gft_admin_token');

  const logout = useCallback(() => {
    localStorage.removeItem('gft_admin_token');
    navigate('/admin/login');
  }, [navigate]);

  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/admin/donations`, { headers }),
      axios.get(`${API}/admin/pledges`, { headers }),
      axios.get(`${API}/admin/contacts`, { headers }),
    ])
      .then(([d, p, c]) => {
        setDonations(d.data);
        setPledges(p.data);
        setContacts(c.data);
      })
      .catch((err) => {
        if (err.response?.status === 401) logout();
      })
      .finally(() => setLoading(false));
  }, [token, navigate, logout]);

  const paidTotal = donations
    .filter((d) => d.status === 'PAID')
    .reduce((sum, d) => sum + Number(d.amount || 0), 0);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500" data-testid="admin-loading">Loading dashboard…</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-xs text-gray-500">Genevieve Fourie Teles Foundation</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/change-password')} data-testid="change-password-nav-button">
              <KeyRound className="w-4 h-4 mr-2" /> Change Password
            </Button>
            <Button variant="outline" onClick={logout} data-testid="admin-logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Log Out
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={TrendingUp} label="Donations Received (Paid)" value={`R${paidTotal.toLocaleString()}`} color="bg-green-100 text-green-600" />
          <StatCard icon={Heart} label="Total Donations" value={donations.length} color="bg-blue-100 text-blue-600" />
          <StatCard icon={HandHeart} label="Total Pledges" value={pledges.length} color="bg-purple-100 text-purple-600" />
          <StatCard icon={Mail} label="Contact Messages" value={contacts.length} color="bg-amber-100 text-amber-600" />
        </div>

        <Tabs defaultValue="donations">
          <TabsList className="mb-6">
            <TabsTrigger value="donations" data-testid="tab-donations">Donations ({donations.length})</TabsTrigger>
            <TabsTrigger value="pledges" data-testid="tab-pledges">Pledges ({pledges.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Messages ({contacts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="donations">
            <div className="flex justify-end mb-3">
              <Button variant="outline" size="sm" data-testid="export-donations-button"
                onClick={() => downloadCsv('donations.csv', donations, [
                  { key: 'created_at', label: 'Date' }, { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' },
                  { key: 'amount', label: 'Amount (ZAR)' }, { key: 'status', label: 'Status' }, { key: 'id', label: 'Reference' },
                ])}>
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>
            <Card className="border-0 shadow-md">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead>
                      <TableHead>Amount</TableHead><TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {donations.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">No donations yet.</TableCell></TableRow>
                    ) : donations.map((d) => (
                      <TableRow key={d.id} data-testid={`donation-row-${d.id}`}>
                        <TableCell className="whitespace-nowrap">{fmtDate(d.created_at)}</TableCell>
                        <TableCell>{d.name || '—'}</TableCell>
                        <TableCell>{d.email || '—'}</TableCell>
                        <TableCell className="font-medium">R{Number(d.amount || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge className={d.status === 'PAID' ? 'bg-green-100 text-green-700' : d.status === 'CANCELLED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{d.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pledges">
            <div className="flex justify-end mb-3">
              <Button variant="outline" size="sm" data-testid="export-pledges-button"
                onClick={() => downloadCsv('pledges.csv', pledges, [
                  { key: 'created_at', label: 'Date' }, { key: 'role', label: 'Role' }, { key: 'fullName', label: 'Name' },
                  { key: 'email', label: 'Email' }, { key: 'phone', label: 'Phone' }, { key: 'organization', label: 'Organization' },
                  { key: 'specialization', label: 'Specialization' }, { key: 'pledgeAmount', label: 'Pledge Amount' },
                  { key: 'availableHours', label: 'Available Hours' }, { key: 'skills', label: 'Skills' }, { key: 'message', label: 'Message' },
                ])}>
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>
            <Card className="border-0 shadow-md">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead><TableHead>Role</TableHead><TableHead>Name</TableHead>
                      <TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pledges.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No pledges yet.</TableCell></TableRow>
                    ) : pledges.map((p) => (
                      <TableRow key={p.id} data-testid={`pledge-row-${p.id}`}>
                        <TableCell className="whitespace-nowrap">{fmtDate(p.created_at)}</TableCell>
                        <TableCell><Badge className="bg-blue-100 text-blue-700 capitalize">{p.role}</Badge></TableCell>
                        <TableCell>{p.fullName}</TableCell>
                        <TableCell>{p.email}</TableCell>
                        <TableCell>{p.phone || '—'}</TableCell>
                        <TableCell className="max-w-xs text-sm text-gray-600">
                          {p.pledgeAmount && <div>Amount: R{Number(p.pledgeAmount).toLocaleString()}</div>}
                          {p.specialization && <div>Spec: {p.specialization}</div>}
                          {p.availableHours && <div>Hours/mo: {p.availableHours}</div>}
                          {p.skills && <div className="truncate">Skills: {p.skills}</div>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="flex justify-end mb-3">
              <Button variant="outline" size="sm" data-testid="export-contacts-button"
                onClick={() => downloadCsv('contact_messages.csv', contacts, [
                  { key: 'created_at', label: 'Date' }, { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' },
                  { key: 'type', label: 'Type' }, { key: 'message', label: 'Message' },
                ])}>
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>
            <Card className="border-0 shadow-md">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-center text-gray-500 py-8">No messages yet.</TableCell></TableRow>
                    ) : contacts.map((c) => (
                      <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                        <TableCell className="whitespace-nowrap">{fmtDate(c.created_at)}</TableCell>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>{c.email}</TableCell>
                        <TableCell className="max-w-md text-sm text-gray-600">{c.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
